import { Component, inject, signal } from '@angular/core';
import { DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MeetingService } from '../../../../../../services/meeting.service';
import { Meeting } from '../../../../../../models/meeting';
import { notePreview, notePreviewTruncated } from '../../../../../../helpers/note-preview';
import {
  ProposeChangesDialogComponent,
  ProposeChangesDialogResult,
} from '../../../../../common/propose-changes-dialog/propose-changes-dialog';

export interface CounselorMeetingDetailsDialogData {
  meeting: Meeting;
}

export type CounselorMeetingDetailsDialogResult =
  | { action: 'canceled' }
  | { action: 'proposed' }
  | undefined;

type DetailsView = 'details' | 'cancel' | 'canceled' | 'addNote' | 'note';

const MAX_NOTE_LENGTH = 2000;

/** Popup showing the full details of a booked meeting, with an option to cancel it. */
@Component({
  selector: 'app-counselor-meeting-details-dialog',
  imports: [
    DatePipe,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
  ],
  templateUrl: './counselor-meeting-details-dialog.html',
  styleUrl: './counselor-meeting-details-dialog.css',
})
export class CounselorMeetingDetailsDialogComponent {
  private meetingService = inject(MeetingService);
  private dialog = inject(MatDialog);
  protected dialogRef = inject(
    MatDialogRef<CounselorMeetingDetailsDialogComponent, CounselorMeetingDetailsDialogResult>,
  );
  protected data = inject<CounselorMeetingDetailsDialogData>(MAT_DIALOG_DATA);

  // Signals because submitCancel() writes to these from inside an HTTP
  // subscribe callback — under zoneless change detection a plain field
  // assigned there never triggers a re-render.
  protected view = signal<DetailsView>('details');
  protected submitting = signal(false);

  protected readonly maxNoteLength = MAX_NOTE_LENGTH;
  protected noteDraft = signal('');
  protected savingNote = signal(false);
  protected noteError = signal<string | null>(null);

  protected get meeting(): Meeting {
    return this.data.meeting;
  }

  /** The notes row preview — see helpers/note-preview.ts. */
  protected get notePreview(): string {
    return notePreview(this.meeting.note);
  }

  /** Whether notePreview actually cut anything — controls the trailing "…". */
  protected get notePreviewTruncated(): boolean {
    return notePreviewTruncated(this.meeting.note);
  }

  /** Switches which view the dialog shows. */
  protected goToView(v: DetailsView): void {
    this.view.set(v);
  }

  /** Opens the note editor, seeded with whatever note is already on the meeting. */
  protected openAddNote(): void {
    this.noteDraft.set(this.meeting.note ?? '');
    this.noteError.set(null);
    this.view.set('addNote');
  }

  /** Saves the note draft and returns to the details view on success. */
  protected saveNote(): void {
    this.savingNote.set(true);
    this.noteError.set(null);
    this.meetingService.updateMeetingNote(this.meeting.id, this.noteDraft()).subscribe({
      next: (updated) => {
        this.savingNote.set(false);
        // Same object reference the bookings list handed in, so this is
        // visible immediately without waiting on a close/refetch.
        this.data.meeting.note = updated.note;
        this.view.set('details');
      },
      error: (err: HttpErrorResponse) => {
        this.savingNote.set(false);
        this.noteError.set(
          err.error?.error || 'Something went wrong saving your note. Please try again.',
        );
      },
    });
  }

  /** Cancels the meeting and swaps to the success view on confirmation. */
  protected submitCancel(): void {
    this.submitting.set(true);
    this.meetingService.cancelMeeting(this.meeting.id).subscribe({
      next: () => {
        this.submitting.set(false);
        this.view.set('canceled');

        // The meeting is already canceled server-side, but the bookings list
        // only learns about it from the close payload. A backdrop or Escape
        // close would otherwise pass undefined and leave the card looking
        // active until a refresh — so take those over and route them
        // through finishCancel().
        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe(() => this.finishCancel());
        this.dialogRef.keydownEvents().subscribe((event) => {
          if (event.key === 'Escape') this.finishCancel();
        });
      },
      error: () => {
        this.submitting.set(false);
      },
    });
  }

  /** Dismisses the cancellation confirmation, handing the result back to the list. */
  protected finishCancel(): void {
    this.dialogRef.close({ action: 'canceled' });
  }

  /**
   * Opens the shared propose changes dialog on top of this one. On success,
   * closes this dialog too so the bookings list reloads with the meeting now
   * awaiting the student's response.
   */
  protected proposeChanges(): void {
    this.dialog
      .open(ProposeChangesDialogComponent, {
        data: { meeting: this.meeting },
        width: '720px',
        maxWidth: '96vw',
      })
      .afterClosed()
      .subscribe((result?: ProposeChangesDialogResult) => {
        if (result?.action === 'proposed') this.dialogRef.close({ action: 'proposed' });
      });
  }
}
